import { useState, type FunctionComponent } from "react";
import { ExportOutlined, MessageOutlined, StarFilled } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Progress, Space, Table, Tag, Typography, type TableProps } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { chatActions, userSelectors } from "@fitness-app/app-store";
import { getTrainersTeam } from "@fitness-app/app-store/src/store/reducers/team/selectors";
import {
  ClientRatingScopeType,
  defaultClientRatingBreakdown,
  GLOBAL_CLIENT_RATING_SCOPE_ID,
  type ClientRatingBreakdown,
  type ClientRatingSnapshot,
} from "@fitness-app/data-models/entities/ClientRating";
import { TraineeStatus, type Trainee } from "@fitness-app/data-models/entities/Trainee";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { createClientsAccess, getClientListTrainerId } from "~/modules/Trainee/clientsAccess";
import { supabase, useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { clientRatingBreakdownLabels } from "./helpers/clientRatingLabels";
import { createTrainerOptions } from "./helpers/createTrainerOptions";

type ClientRatingRow = ClientRatingSnapshot & {
  trainee: Pick<
    Trainee,
    "id" | "userId" | "email" | "firstName" | "lastName" | "assignedTrainerId" | "createdAt"
  > | null;
};

type Props = {
  isSearchMode: boolean;
};

const pageSize = 25;

const ClientRatingTable: FunctionComponent<Props> = ({ isSearchMode }) => {
  const { t } = useTranslation("trainees");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const trainers = useAppSelector(getTrainersTeam);
  const fetchingExtraChannel = useAppSelector((store) => store.chat.channelsListStatus.fetchingExtraChannel);
  const { memberId } = useUserClaims();
  const permissions = useAppSelector(userSelectors.getUserPermissions);
  const access = createClientsAccess(permissions);
  const [page, setPage] = useState(1);
  const [assignedTrainerFilter, setAssignedTrainerFilter] = useState<string | null>(null);

  // Zawężenie do własnych podopiecznych wynika ze scope'u clients.read, a nie
  // z roli — head coach, administracja i ACCOUNT_OWNER filtrują swobodnie.
  const effectiveTrainerId = getClientListTrainerId(access, memberId, assignedTrainerFilter);

  const ratingsQuery = useQuery({
    queryKey: ["client-rating-snapshot", page, effectiveTrainerId],
    keepPreviousData: true,
    queryFn: async () => {
      let query = supabase
        .from("client_rating_snapshot")
        .select(
          "*, trainee:traineeId!inner(id, userId, email, firstName, lastName, assignedTrainerId, createdAt, status)",
          { count: "exact" },
        )
        .eq("trainee.status", TraineeStatus.ACTIVE)
        .eq("scopeType", ClientRatingScopeType.Global)
        .eq("scopeId", GLOBAL_CLIENT_RATING_SCOPE_ID)
        .order("ratingPoints", { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (effectiveTrainerId === "none") {
        query = query.is("trainee.assignedTrainerId", null);
      } else if (effectiveTrainerId) {
        query = query.eq("trainee.assignedTrainerId", effectiveTrainerId);
      }

      const { data, error, count } = await query.returns<ClientRatingRow[]>();

      if (error) {
        throw error;
      }

      return {
        data: data ?? [],
        count: count ?? 0,
      };
    },
  });

  const handleChange: TableProps<ClientRatingRow>["onChange"] = (pagination, filters) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }

    if (access.canFilterByTrainer) {
      const nextTrainerFilter = (filters.assignedTrainer?.[0] as string | undefined) ?? null;
      if (nextTrainerFilter !== assignedTrainerFilter) {
        setAssignedTrainerFilter(nextTrainerFilter);
        setPage(1);
      }
    }
  };

  return (
    <Table<ClientRatingRow>
      dataSource={ratingsQuery.data?.data ?? []}
      rowKey="traineeId"
      size="middle"
      scroll={{ x: true }}
      loading={ratingsQuery.isFetching}
      locale={{
        emptyText: isSearchMode ? "Brak wyników dla wyszukiwania" : "Brak przeliczonych ratingów klientek",
      }}
      pagination={{
        current: page,
        pageSize,
        total: ratingsQuery.data?.count ?? 0,
        pageSizeOptions: [String(pageSize)],
        showSizeChanger: false,
      }}
      onChange={handleChange}
    >
      <Table.Column<ClientRatingRow>
        title="#"
        width={60}
        render={(_name, _row, i) => (page > 1 ? (page - 1) * pageSize + i + 1 : i + 1)}
      />
      <Table.Column<ClientRatingRow>
        title="Klient"
        dataIndex="trainee"
        key="trainee"
        render={(_, row) => (
          <Space direction="horizontal" size={0}>
            <Button type="link" className="px-0" onClick={() => navigate(`/trainee/details/${row.trainee?.id}`)}>
              {row.trainee?.firstName} {row.trainee?.lastName}
            </Button>
            {row.trainee?.userId && (
              <Button
                icon={<MessageOutlined />}
                style={{ paddingLeft: 2, paddingRight: 2 }}
                title="Wyślij wiadomość"
                onClick={() =>
                  row.trainee?.userId
                    ? dispatch(chatActions.findAndAddChannelToList({ userId: row.trainee.userId }))
                    : null
                }
                type="link"
                loading={fetchingExtraChannel}
              />
            )}
          </Space>
        )}
      />
      <Table.Column<ClientRatingRow>
        title="Email"
        dataIndex="trainee"
        key="email"
        ellipsis
        render={(trainee: ClientRatingRow["trainee"]) => (
          <Typography.Link href={`mailto:${trainee?.email}`} target="_blank" copyable>
            {trainee?.email}
          </Typography.Link>
        )}
      />
      {access.canFilterByTrainer && (
        <Table.Column<ClientRatingRow>
          title="Trener"
          dataIndex={["trainee", "assignedTrainerId"]}
          key="assignedTrainer"
          filters={createTrainerOptions(trainers, t, false, memberId)}
          filteredValue={assignedTrainerFilter ? [assignedTrainerFilter] : null}
          render={(assignedTrainerId) => {
            const trainer = trainers.find((trainer) => trainer.id === assignedTrainerId);
            return trainer ? `${trainer.firstName} ${trainer.lastName}` : "-";
          }}
        />
      )}
      <Table.Column<ClientRatingRow>
        title="Rating"
        dataIndex="ratingPoints"
        key="ratingPoints"
        sorter={(a, b) => a.ratingPoints - b.ratingPoints}
        render={(ratingPoints, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong style={{ whiteSpace: "nowrap" }}>
              <StarFilled style={{ color: "#faad14", marginRight: 4 }} />
              {ratingPoints.toLocaleString("pl-PL")} pkt
            </Typography.Text>
            <Typography.Text type="secondary">Level {row.level}</Typography.Text>
          </Space>
        )}
      />
      <Table.Column<ClientRatingRow>
        title="Do kolejnego levelu"
        dataIndex="pointsToNextLevel"
        key="pointsToNextLevel"
        render={(pointsToNextLevel, row) => {
          const progress =
            row.nextLevel && pointsToNextLevel !== null
              ? Math.min(100, Math.round((row.ratingPoints / (row.ratingPoints + pointsToNextLevel)) * 100))
              : 100;

          return (
            <Space direction="vertical" size={0} style={{ minWidth: 160 }}>
              <Progress percent={progress} size="small" showInfo={false} strokeColor="#faad14" />
              <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                {row.nextLevel && pointsToNextLevel !== null
                  ? `${pointsToNextLevel.toLocaleString("pl-PL")} pkt do L${row.nextLevel}`
                  : "Najwyższy level"}
              </Typography.Text>
            </Space>
          );
        }}
      />
      <Table.Column<ClientRatingRow>
        title="Punkty"
        key="points"
        render={(_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ whiteSpace: "nowrap" }}>
              Całość: {row.lifetimePoints.toLocaleString("pl-PL")} pkt
            </Typography.Text>
            <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
              Ostatnie 30 dni: +{row.recentPoints.toLocaleString("pl-PL")} pkt
            </Typography.Text>
          </Space>
        )}
      />
      <Table.Column<ClientRatingRow>
        title="Główne aktywności"
        dataIndex="breakdown"
        key="breakdown"
        render={(breakdown: ClientRatingBreakdown | null) => {
          const top = Object.entries(breakdown ?? defaultClientRatingBreakdown)
            .filter(([, points]) => points > 0)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3);

          return top.length ? (
            <Space wrap size={4}>
              {top.map(([key, points]) => (
                <Tag color="gold" key={key}>
                  {clientRatingBreakdownLabels[key as keyof ClientRatingBreakdown]}: {points}
                </Tag>
              ))}
            </Space>
          ) : (
            "-"
          );
        }}
      />
      <Table.Column<ClientRatingRow>
        title="Opcje"
        dataIndex="action"
        key="action"
        render={(_, row) => (
          <Space size={4}>
            <Button type="link" className="px-0" onClick={() => navigate(`/trainee/details/${row.trainee?.id}`)}>
              Szczegóły
            </Button>
            <Button
              icon={<ExportOutlined />}
              title="Otwórz w nowy oknie"
              href={`${window.location.origin}/trainee/details/${row.trainee?.id}`}
              target="_blank"
              type="link"
            />
          </Space>
        )}
      />
    </Table>
  );
};

export default ClientRatingTable;
